import { useEffect, useRef, useState } from "react";
import "./styles.scss";
import ContactModal from "./contactModal";
import ContactMappingModal from "./contactMappingModal";
import Confirm from "../Generic/ConfirmDialog";
import DataTable, { dataColumn } from "../Generic/DataTable";
import { ContactDTO, DeleteContact, GetAllContacts } from "../../Logic/EmsAPI/Contacts";

// alphabetical by name, ignoring case; contacts with no name (legacy rows) go last
function byName(a: ContactDTO, b: ContactDTO): number {
    if (!a.name) return b.name ? 1 : 0;
    if (!b.name) return -1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

// a contact matches when its name or email address contains the filter text, ignoring case
function matchesFilter(contact: ContactDTO, filter: string): boolean {
    const text = filter.trim().toLowerCase();
    if (text === "") return true;
    return (contact.name ?? "").toLowerCase().includes(text) || contact.emailAddress.toLowerCase().includes(text);
}

// the modal is closed, adding a new contact, or editing the given one
type ModalState = { open: false } | { open: true; contact?: ContactDTO };

function ContactMaintenance() {
    const [contacts, setContacts] = useState<ContactDTO[]>([]);
    const [modal, setModal] = useState<ModalState>({ open: false });
    const [filter, setFilter] = useState("");
    const [mappingContact, setMappingContact] = useState<ContactDTO>();
    const [deletingContact, setDeletingContact] = useState<ContactDTO>();
    // Confirm also fires on Enter, so a quick double press must not send two deletes
    const deleteInProgress = useRef(false);

    // contacts is already in name order, so the filtered list keeps it
    const visibleContacts = contacts.filter(contact => matchesFilter(contact, filter));

    const loadContacts = async () => {
        const response = await GetAllContacts();
        if (response.isOk && response.data) {
            setContacts([...response.data.items].sort(byName));
        }
    };

    useEffect(() => {
        loadContacts();
    }, []);

    const buttonColumn = (
        key: string,
        label: string,
        onClick: (contact: ContactDTO) => void,
        style = "g-btn-alternate",
    ): dataColumn<ContactDTO> => ({
        title: <></>,
        key,
        getData: contact => (
            <button
                className={`g-btn ${style}`}
                onClick={evt => {
                    // the row's own click opens Edit, so a button click must not reach it
                    evt.stopPropagation();
                    onClick(contact);
                }}
            >
                {label}
            </button>
        ),
        width: "110px",
        minWidth: "110px",
        canResize: false,
        sort: undefined,
        textAlignment: "center",
        visible: true,
    });

    const textColumn = (
        key: string,
        title: string,
        getText: (contact: ContactDTO) => string,
    ): dataColumn<ContactDTO> => ({
        title: <>{title}</>,
        key,
        getData: contact => <>{getText(contact)}</>,
        getDataToolTip: getText,
        width: "35%",
        minWidth: "150px",
        canResize: false,
        sort: undefined,
        textAlignment: "left",
        visible: true,
    });

    const editContact = (contact: ContactDTO) => setModal({ open: true, contact });

    // CallEmsApi reports failures itself; reloading either way shows what the server now holds
    const deleteContact = async (contact: ContactDTO) => {
        if (deleteInProgress.current) return;
        deleteInProgress.current = true;
        await DeleteContact(contact.id);
        deleteInProgress.current = false;
        setDeletingContact(undefined);
        await loadContacts();
    };

    // DataTable reports the clicked row by its uniqueIdentifiyer, the contact ID
    const onRowClick = (id: string) => {
        const contact = contacts.find(x => x.id.toString() === id);
        if (contact) editContact(contact);
    };

    const columns: dataColumn<ContactDTO>[] = [
        textColumn("name", "Name", contact => contact.name ?? ""),
        textColumn("emailAddress", "Email Address", contact => contact.emailAddress),
        buttonColumn("edit", "Edit", editContact),
        buttonColumn("mappings", "Mappings", setMappingContact),
        buttonColumn("delete", "Delete", setDeletingContact, "g-btn-error"),
    ];

    return (
        <>
            <div
                className="header"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                <span>Contact Maintenance</span>
                <button
                    className="g-btn g-btn-action"
                    // 9pt matches Edit Profile; the negative margins sit the button in the header's padding,
                    // so the header stays as tall as the Preferences one and their bottom borders line up
                    style={{ fontSize: "9pt", margin: "-10px 0" }}
                    onClick={() => setModal({ open: true })}
                >
                    Add Contact
                </button>
            </div>
            <div style={{ padding: "10px 20px" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "10px",
                        maxWidth: "400px",
                    }}
                >
                    <label
                        className="g-label"
                        htmlFor="contact-maintenance-filter"
                    >
                        Filter:
                    </label>
                    <input
                        id="contact-maintenance-filter"
                        type="text"
                        className="g-TextBox-default"
                        aria-label="Filter contacts"
                        value={filter}
                        onChange={e => setFilter(e.currentTarget.value)}
                        autoComplete="off"
                        aria-autocomplete="none"
                    />
                </div>
                <DataTable
                    data={visibleContacts}
                    // DataTable puts the theme into the table's class, so this adds the page's header fix
                    theme="dataTable_default contact-table"
                    dataColumns={columns}
                    uniqueIdentifiyer={contact => contact.id.toString()}
                    onRowClick={onRowClick}
                    selectedItems={[]}
                    selectedItem=""
                    contextMenu={() => {}}
                    keyPress={() => {}}
                    lastItemInteracted={0}
                />
                {visibleContacts.length === 0 && contacts.length > 0 && (
                    <div style={{ padding: "10px", fontSize: "12px" }}>No contacts match the filter.</div>
                )}
            </div>
            {modal.open && (
                <ContactModal
                    contact={modal.contact}
                    onClose={() => setModal({ open: false })}
                    onSaved={() => loadContacts()}
                />
            )}
            {mappingContact && (
                <ContactMappingModal
                    contact={mappingContact}
                    onClose={() => setMappingContact(undefined)}
                />
            )}
            {deletingContact && (
                <Confirm
                    title="Delete contact"
                    messageType="warn"
                    oKText="Delete"
                    onConfirm={() => deleteContact(deletingContact)}
                    onCancel={() => setDeletingContact(undefined)}
                >
                    <>
                        <p>
                            Delete <strong>{deletingContact.name || deletingContact.emailAddress}</strong>
                            {deletingContact.name ? ` (${deletingContact.emailAddress})` : ""}?
                        </p>
                        <p>
                            Its mailbox mappings are removed too, so new emails from this address are no longer
                            assigned automatically. This can&apos;t be undone.
                        </p>
                    </>
                </Confirm>
            )}
        </>
    );
}
export default ContactMaintenance;
